import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  RestartAlt as ReverseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  AccountBalanceWallet as LedgerIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Refresh as RunIcon,
  FilterAltOff as ClearFiltersIcon,
  Search as SearchIcon,
  AutoAwesome as DraftIcon,
  CheckCircle as PostedIcon,
  ThumbUp as ApprovedIcon,
  Undo as ReversedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import { ledgerAPI } from '../../services/api';
import { getLedgerId, normalizeJournal, normalizeJournalLines, normalizeJournals } from '../../utils/ledgerIds';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import ModulePageHeader from '../common/ModulePageHeader';
import { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import JournalFormDialog, { emptyJournalLine } from './forms/JournalFormDialog';
import {
  AccountPicker,
  CostCentrePicker,
  CustomerPicker,
  DepartmentPicker,
  FiscalPeriodPicker,
  LocationPicker,
  SupplierPicker,
  useLedgerOptions,
} from './LedgerPickers';

const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const unwrapList = (response) => {
  const payload = unwrap(response);
  if (Array.isArray(payload)) return { items: payload, total: payload.length, page: 1, perPage: payload.length };
  return {
    items: payload.items ?? payload.entries ?? [],
    total: Number(payload.total ?? 0),
    page: Number(payload.page ?? 1),
    perPage: Number(payload.limit ?? 15),
  };
};
const unwrapRawList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

const formatAmount = (v) =>
  (Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Same input-polish helpers as JournalFormDialog: no spinner arrows, wheel
// never changes a number field's value, comma-grouped amounts once you
// leave the field, and Enter/ArrowDown/ArrowUp move between plain text and
// number inputs (Left/Right stay native so in-field cursor movement isn't
// broken; selects, date pickers and Autocomplete-based pickers already have
// their own meaning for arrow keys, so this is never attached to those).
const noSpinnerSx = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
};
const stopWheelChange = (e) => e.target.blur();
const focusAdjacentInput = (current, direction) => {
  const root = current.closest('[role="dialog"]') || document;
  const focusables = Array.from(root.querySelectorAll('input:not([disabled]):not([type="hidden"])')).filter(
    (el) => el.offsetParent !== null
  );
  const idx = focusables.indexOf(current);
  if (idx === -1) return;
  const target = focusables[idx + direction];
  if (target) {
    target.focus();
    if (target.select) target.select();
  }
};
const handleGridKeyDown = (e) => {
  if (e.key === 'Enter' || e.key === 'ArrowDown') {
    e.preventDefault();
    focusAdjacentInput(e.currentTarget, 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    focusAdjacentInput(e.currentTarget, -1);
  }
};
const parseAmountInput = (raw) => raw.replace(/,/g, '');

const formatDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString();
};

const totalsOf = (e) => ({
  debit: Number(e.total_debit ?? e.totalDebit ?? e.debit_amount ?? 0) || 0,
  credit: Number(e.total_credit ?? e.totalCredit ?? e.credit_amount ?? 0) || 0,
});

// ---------------------------------------------------------------------------
// Human-readable labels — the UI must never show raw database ids.
// The ledger embeds relations on entries/lines (period, creator, account,
// customer, supplier, currency); master data below that is resolved through
// the same cached lookups the pickers use.
// ---------------------------------------------------------------------------

export const personName = (u) => {
  if (!u) return '';
  const full = [u.first_name ?? u.firstName, u.last_name ?? u.lastName].filter(Boolean).join(' ').trim();
  return full || u.email || u.name || '';
};

const codeNameOf = (item, codeKeys, nameKeys) => {
  if (!item) return '';
  const code = codeKeys.map((k) => item[k]).find((v) => v !== undefined && v !== null && v !== '') || '';
  const name = nameKeys.map((k) => item[k]).find((v) => v !== undefined && v !== null && v !== '') || '';
  return [code, name].filter(Boolean).join(' - ').trim();
};

export const useLedgerLookups = () => {
  const { options: accountsRaw } = useLedgerOptions('accounts-raw', () =>
    ledgerAPI.getAccounts({ limit: 100 }).then(unwrapRawList)
  );
  const { options: periodsRaw } = useLedgerOptions('lookup-periods', () =>
    ledgerAPI.getFiscalPeriods({ limit: 100 }).then(unwrapRawList)
  );
  const { options: departmentsRaw } = useLedgerOptions('lookup-departments', () =>
    ledgerAPI.getDepartments({ limit: 100 }).then(unwrapRawList)
  );
  const { options: locationsRaw } = useLedgerOptions('lookup-locations', () =>
    ledgerAPI.getLocations({ limit: 100 }).then(unwrapRawList)
  );
  const { options: costCentresRaw } = useLedgerOptions('lookup-cost-centres', () =>
    ledgerAPI.getCostCentres({ limit: 100 }).then(unwrapRawList)
  );
  const { options: currenciesRaw } = useLedgerOptions('lookup-currencies', () =>
    ledgerAPI.getCurrencies({ limit: 100 }).then(unwrapRawList)
  );
  // NOTE: these use `lookup-*` keys deliberately — LedgerPickers.js already
  // caches `customers` / `suppliers` / `inventory-items` as mapped
  // {value,label} options, while these lookups need the raw rows. Sharing a
  // key poisons the pickers (blank labels, selection resolves to undefined).
  const { options: customersRaw } = useLedgerOptions('lookup-customers', () =>
    ledgerAPI.getCustomers({ limit: 100 }).then(unwrapRawList)
  );
  const { options: suppliersRaw } = useLedgerOptions('lookup-suppliers', () =>
    ledgerAPI.getSuppliers({ limit: 100 }).then(unwrapRawList)
  );
  const { options: productsRaw } = useLedgerOptions('lookup-inventory-items', () =>
    ledgerAPI.getInventoryItems({ limit: 100 }).then(unwrapRawList)
  );

  return useMemo(() => {
    const indexById = (list, keys) => {
      const m = new Map();
      (list || []).forEach((item) => {
        for (const k of keys) {
          if (item?.[k] !== undefined && item?.[k] !== null) {
            m.set(String(item[k]), item);
            break;
          }
        }
      });
      return m;
    };
    const accounts = indexById(accountsRaw, ['account_id', 'id']);
    const periods = indexById(periodsRaw, ['period_id', 'id']);
    const departments = indexById(departmentsRaw, ['department_id', 'id']);
    const locations = indexById(locationsRaw, ['location_id', 'id']);
    const costCentres = indexById(costCentresRaw, ['cost_centre_id', 'cost_center_id', 'id']);
    const currencies = indexById(currenciesRaw, ['currency_id', 'id']);
    const customers = indexById(customersRaw, ['customer_id', 'id']);
    const suppliers = indexById(suppliersRaw, ['supplier_id', 'id']);
    const products = indexById(productsRaw, ['item_id', 'id']);
    const pick = (map, id) => (id === '' || id === null || id === undefined ? null : map.get(String(id)) ?? null);

    return {
      accountLabel: (id, fallback = '') => {
        const a = pick(accounts, id);
        return codeNameOf(a, ['account_code'], ['account_name']) || fallback;
      },
      periodLabel: (id, fallback = '') => pick(periods, id)?.period_name || fallback,
      departmentLabel: (id, fallback = '') => {
        const d = pick(departments, id);
        return codeNameOf(d, ['department_code'], ['department_name']) || fallback;
      },
      locationLabel: (id, fallback = '') => {
        const l = pick(locations, id);
        return codeNameOf(l, ['location_code'], ['location_name']) || fallback;
      },
      costCentreLabel: (id, fallback = '') => {
        const c = pick(costCentres, id);
        return codeNameOf(c, ['cost_centre_code', 'cost_center_code'], ['cost_centre_name', 'cost_center_name']) || fallback;
      },
      currencyLabel: (id, fallback = '') => {
        const c = pick(currencies, id);
        return codeNameOf(c, ['currency_code'], ['currency_name']) || fallback;
      },
      currencyCode: (id, fallback = '') => pick(currencies, id)?.currency_code || fallback,
      customerLabel: (id, fallback = '') => pick(customers, id)?.name || fallback,
      supplierLabel: (id, fallback = '') => pick(suppliers, id)?.name || fallback,
      productLabel: (id, fallback = '') => {
        const p = pick(products, id);
        return codeNameOf(p, ['item_code'], ['item_name']) || fallback;
      },
    };
  }, [accountsRaw, periodsRaw, departmentsRaw, locationsRaw, costCentresRaw, currenciesRaw, customersRaw, suppliersRaw, productsRaw]);
};

// Account narration for a journal line: embedded relation first, then lookup.
const lineAccountLabel = (l, look) => {
  if (l?.account) return codeNameOf(l.account, ['account_code'], ['account_name']);
  if (l?.account_code) return [l.account_code, l.account_name].filter(Boolean).join(' - ');
  return look.accountLabel(l?.account_id);
};

// ---------------------------------------------------------------------------
// Status chip (OAS pattern: outlined chip with status icon)
// ---------------------------------------------------------------------------

const JournalStatusChip = ({ status }) => {
  const st = (status || 'DRAFT').toUpperCase();
  const cfg = {
    DRAFT: { label: 'Draft', color: 'default', icon: <DraftIcon fontSize="small" /> },
    POSTED: { label: 'Posted', color: 'info', icon: <PostedIcon fontSize="small" /> },
    APPROVED: { label: 'Approved', color: 'success', icon: <ApprovedIcon fontSize="small" /> },
    REVERSED: { label: 'Reversed', color: 'warning', icon: <ReversedIcon fontSize="small" /> },
    VOID: { label: 'Void', color: 'error', icon: <VoidIcon fontSize="small" /> },
    PENDING: { label: 'Pending', color: 'warning', icon: <DraftIcon fontSize="small" /> },
    ACTIVE: { label: 'Active', color: 'success', icon: <ApprovedIcon fontSize="small" /> },
    PAUSED: { label: 'Paused', color: 'default', icon: <PauseIcon fontSize="small" /> },
  }[st] || { label: status || 'Draft', color: 'default', icon: <DraftIcon fontSize="small" /> };
  return <Chip icon={cfg.icon} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

// Row actions as a "⋮" dropdown with word labels — an icon-only row forces
// a hover just to find out what a button does; a menu shows every action's
// name up front and scales better as more workflow actions apply per status.
const JournalRowActionsMenu = ({
  entry, canUpdate, canPost, canApprove, canCreate, canDelete,
  onView, onEdit, onPost, onDelete, onApprove, onVoid, onReverse, onRequestReversal,
}) => {
  const [anchor, setAnchor] = useState(null);
  const st = (entry.status || '').toUpperCase();
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor} open={Boolean(anchor)} onClose={close}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ elevation: 2, sx: { minWidth: 190 } }}
      >
        <MenuItem onClick={() => { close(); onView(); }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          View lines
        </MenuItem>
        {st === 'DRAFT' && canUpdate && (
          <MenuItem onClick={() => { close(); onEdit(); }}>
            <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
            Edit draft
          </MenuItem>
        )}
        {st === 'DRAFT' && canPost && (
          <MenuItem onClick={() => { close(); onPost(); }}>
            <ListItemIcon><PostIcon fontSize="small" color="primary" /></ListItemIcon>
            Post
          </MenuItem>
        )}
        {st === 'DRAFT' && canDelete && [
          <Divider key="d-delete" />,
          <MenuItem key="delete" onClick={() => { close(); onDelete(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            Delete draft
          </MenuItem>,
        ]}
        {(st === 'POSTED' || st === 'APPROVED') && canApprove && st === 'POSTED' && (
          <MenuItem onClick={() => { close(); onApprove(); }}>
            <ListItemIcon><ApproveIcon fontSize="small" color="primary" /></ListItemIcon>
            Approve
          </MenuItem>
        )}
        {(st === 'POSTED' || st === 'APPROVED') && canApprove && <Divider key="d-workflow" />}
        {(st === 'POSTED' || st === 'APPROVED') && canApprove && (
          <MenuItem onClick={() => { close(); onVoid(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><VoidIcon fontSize="small" color="error" /></ListItemIcon>
            Void (needs reason)
          </MenuItem>
        )}
        {(st === 'POSTED' || st === 'APPROVED') && canApprove && (
          <MenuItem onClick={() => { close(); onReverse(); }} sx={{ color: 'warning.main' }}>
            <ListItemIcon><ReverseIcon fontSize="small" color="warning" /></ListItemIcon>
            Reverse now
          </MenuItem>
        )}
        {(st === 'POSTED' || st === 'APPROVED') && canCreate && (
          <MenuItem onClick={() => { close(); onRequestReversal(); }}>
            <ListItemIcon><LedgerIcon fontSize="small" /></ListItemIcon>
            Request reversal
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

// Same "⋮" word-labeled menu pattern for the recurring templates table.
const RecurringRowActionsMenu = ({ template, canUpdate, canDelete, onEdit, onTogglePause, onDelete }) => {
  const [anchor, setAnchor] = useState(null);
  const paused = (template.status || '').toUpperCase() === 'PAUSED';
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor} open={Boolean(anchor)} onClose={close}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ elevation: 2, sx: { minWidth: 190 } }}
      >
        {canUpdate && (
          <MenuItem onClick={() => { close(); onEdit(); }}>
            <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
            Edit
          </MenuItem>
        )}
        {canUpdate && (
          <MenuItem onClick={() => { close(); onTogglePause(); }}>
            <ListItemIcon>{paused ? <ResumeIcon fontSize="small" color="success" /> : <PauseIcon fontSize="small" />}</ListItemIcon>
            {paused ? 'Resume' : 'Pause'}
          </MenuItem>
        )}
        {canDelete && [
          <Divider key="d-delete" />,
          <MenuItem key="delete" onClick={() => { close(); onDelete(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            Delete (only if never generated)
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

// Vertical label-above-value row used by the detail dialog so it reads like
// the entry form sections (one field per row, label on top, value below).
const DetailField = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" fontWeight={600}>{children}</Typography>
  </Box>
);

// Journal dialogs hold unsaved input (void reasons, new lines, lookups): a
// stray click outside must not discard them. Escape still closes.
const noBackdrop = (close) => (event, reason) => {
  if (reason === 'backdropClick') return;
  close();
};

// ---------------------------------------------------------------------------
// Constants (mirror accounting-apis enums)
// ---------------------------------------------------------------------------

const CREATABLE_ENTRY_TYPES = ['GENERAL', 'ADJUSTMENT', 'RECLASSIFICATION'];
const ALL_ENTRY_TYPES = ['GENERAL', 'ADJUSTMENT', 'RECLASSIFICATION', 'OPENING', 'CLOSING', 'REVERSAL'];
const ENTRY_STATUSES = ['DRAFT', 'POSTED', 'APPROVED', 'VOID', 'REVERSED'];
const SOURCE_MODULES = [
  'MANUAL', 'INVOICE', 'PAYMENT', 'PURCHASE_INVOICE', 'SALES_INVOICE', 'SALES_PAYMENT',
  'PURCHASE_PAYMENT', 'PAYROLL', 'INVENTORY', 'DEPRECIATION', 'ACCRUAL',
  'BANK_RECONCILIATION', 'ACCOUNT_SETUP', 'CREDIT_DEBIT_NOTE', 'TAX_RETURN',
  'FX_VALUATION', 'FISCAL_CLOSE', 'EXPENSE_CLAIM', 'EXPENSE_REIMBURSEMENT',
  'REVENUE_RECOGNITION', 'COST_ALLOCATION', 'CASH_RECEIPT', 'CASH_PAYMENT',
  'RECURRING_JOURNAL',
];

const numOrUndefined = (v) => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const emptyForm = () => ({
  entry_number: '',
  entry_type: 'GENERAL',
  entry_date: new Date().toISOString().slice(0, 10),
  period_id: '',
  reference: '',
  description: '',
  source_module: '',
  source_id: '',
  lines: [emptyJournalLine(1), emptyJournalLine(2)],
});

const cleanLineForPayload = (l, i, forUpdate) => {
  const line = {
    line_number: l.line_number ?? i + 1,
    account_id: Number(l.account_id),
    debit_amount: Number(l.debit_amount) || 0,
    credit_amount: Number(l.credit_amount) || 0,
  };
  if (forUpdate && l.line_id) line.line_id = Number(l.line_id);
  if (l.description?.trim()) line.description = l.description.trim();
  if (l.reference?.trim()) line.reference = l.reference.trim();
  const dept = numOrUndefined(l.department_id);
  if (dept !== undefined) line.department_id = dept;
  const loc = numOrUndefined(l.location_id);
  if (loc !== undefined) line.location_id = loc;
  const cc = numOrUndefined(l.cost_center_id);
  if (cc !== undefined) line.cost_center_id = cc;
  const cust = numOrUndefined(l.customer_id);
  if (cust !== undefined) line.customer_id = cust;
  const supp = numOrUndefined(l.supplier_id);
  if (supp !== undefined) line.supplier_id = supp;
  const prod = numOrUndefined(l.product_id);
  if (prod !== undefined) line.product_id = prod;
  const fx = numOrUndefined(l.foreign_currency_id);
  if (fx !== undefined) line.foreign_currency_id = fx;
  const fd = numOrUndefined(l.foreign_debit);
  if (fd !== undefined) line.foreign_debit = fd;
  const fc = numOrUndefined(l.foreign_credit);
  if (fc !== undefined) line.foreign_credit = fc;
  const xr = numOrUndefined(l.exchange_rate);
  if (xr !== undefined) line.exchange_rate = xr;
  return line;
};

const toJournalPayload = (values, forUpdate = false) => {
  const lines = (values.lines || [])
    .filter((l) => l.account_id && (Number(l.debit_amount) > 0 || Number(l.credit_amount) > 0))
    .map((l, i) => cleanLineForPayload(l, i, forUpdate));
  const payload = { entry_type: values.entry_type, description: (values.description || '').trim(), lines };
  if (values.entry_number?.trim()) payload.entry_number = values.entry_number.trim();
  if (values.entry_date) payload.entry_date = values.entry_date;
  const periodId = numOrUndefined(values.period_id);
  if (periodId !== undefined) payload.period_id = periodId;
  if (values.reference?.trim()) payload.reference = values.reference.trim();
  if (values.source_module) payload.source_module = values.source_module;
  const sourceId = numOrUndefined(values.source_id);
  if (sourceId !== undefined) payload.source_id = sourceId;
  return { payload, lines };
};

const EMPTY_FILTERS = {
  search: '',
  entry_number: '',
  entry_type: '',
  status: '',
  period_id: '',
  account_id: '',
  source_module: '',
  start_date: '',
  end_date: '',
  min_amount: '',
  max_amount: '',
};

const emptyLedgerDlg = () => ({
  open: false,
  accountId: '',
  header: null,
  rows: [],
  opening: 0,
  closing: 0,
  totalDebits: 0,
  totalCredits: 0,
  loading: false,
  start_date: '',
  end_date: '',
});

// ---------------------------------------------------------------------------
// Entries tab (OAS-inspired: bulk bar + rich filters + stacked-cell table)
// ---------------------------------------------------------------------------

const EntriesTab = ({ can, showSnackbar, confirm }) => {
  const resource = RESOURCES.LEDGER.GL;
  const canCreate = can(resource, ACTIONS.CREATE);
  const canUpdate = can(resource, ACTIONS.UPDATE);
  const canDelete = can(resource, ACTIONS.DELETE);
  const canApprove = can(resource, ACTIONS.APPROVE);
  const canPost = canUpdate || canApprove;
  const look = useLedgerLookups();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLines, setDetailLines] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedLines, setExpandedLines] = useState(new Set());
  const [newLine, setNewLine] = useState(emptyJournalLine(1));

  const [voidDlg, setVoidDlg] = useState({ open: false, entry: null, reason: '' });
  const [reverseDlg, setReverseDlg] = useState({ open: false, entry: null, reversal_date: '', reversal_description: '' });
  const [requestDlg, setRequestDlg] = useState({ open: false, entry: null, reason: '', reversal_date: '' });
  const [ledgerDlg, setLedgerDlg] = useState(emptyLedgerDlg());
  const [findDlg, setFindDlg] = useState({ open: false, number: '', loading: false });

  const hasActiveFilters = useMemo(() => Object.values(filters).some((v) => v !== '' && v !== null && v !== undefined), [filters]);

  // Debounced free-text search (OAS 500ms pattern).
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((p) => (p.search === searchInput ? p : { ...p, search: searchInput }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const load = useCallback(async () => {
    try {
      if (items.length === 0) setLoading(true);
      else setFetching(true);
      const params = { page, limit: size };
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        if (['min_amount', 'max_amount', 'period_id', 'account_id'].includes(k) && v !== '') {
          const n = Number(v);
          if (Number.isFinite(n)) params[k] = n;
          return;
        }
        params[k] = v;
      });
      const res = await ledgerAPI.getJournals(params);
      const { items: raw, total: t } = unwrapList(res);
      setItems(normalizeJournals(raw));
      setTotal(t);
      setSelected((prev) => prev.filter((id) => raw.some((e) => (e.journal_entry_id ?? e.entry_id ?? e.id) === id)));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch journals'), 'error');
    } finally {
      setLoading(false);
      setFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (entry) => {
    const e = normalizeJournal(entry);
    setDetail(e);
    setDetailLines([]);
    setExpandedLines(new Set());
    setDetailLoading(true);
    try {
      const [fullRes, linesRes] = await Promise.allSettled([
        ledgerAPI.getJournal(e.id),
        ledgerAPI.getJournalLines(e.id),
      ]);
      if (fullRes.status === 'fulfilled') {
        const full = normalizeJournal(unwrap(fullRes.value));
        setDetail({ ...e, ...full, id: e.id });
        const embedded = Array.isArray(full.lines) && full.lines.length ? normalizeJournalLines(full.lines) : null;
        if (embedded) setDetailLines(embedded);
      }
      if (linesRes.status === 'fulfilled') {
        const fetched = normalizeJournalLines(unwrapList(linesRes.value).items);
        if (fetched.length) setDetailLines(fetched);
      }
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch journal lines'), 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setValues(emptyForm());
    setShowForm(true);
  };

  const openEdit = async (entry) => {
    const e = normalizeJournal(entry);
    let lines = [];
    try {
      const res = await ledgerAPI.getJournalLines(e.id);
      lines = normalizeJournalLines(unwrapList(res).items).map((l, i) => ({
        line_id: l.id,
        line_number: l.line_number ?? i + 1,
        account_id: l.account_id ?? '',
        debit_amount: Number(l.debit_amount ?? l.debit ?? 0) || 0,
        credit_amount: Number(l.credit_amount ?? l.credit ?? 0) || 0,
        description: l.description || '',
        reference: l.reference || '',
        department_id: l.department_id ?? '',
        location_id: l.location_id ?? '',
        cost_center_id: l.cost_center_id ?? '',
        customer_id: l.customer_id ?? '',
        supplier_id: l.supplier_id ?? '',
        product_id: l.product_id ?? '',
        foreign_currency_id: l.foreign_currency_id ?? '',
        foreign_debit: l.foreign_debit ?? '',
        foreign_credit: l.foreign_credit ?? '',
        exchange_rate: l.exchange_rate ?? '',
      }));
    } catch {
      lines = [];
    }
    if (lines.length < 2) {
      lines = [emptyJournalLine(1), emptyJournalLine(2)];
    }
    setEditing(e);
    setValues({
      entry_number: e.entry_number || '',
      entry_type: e.entry_type || 'GENERAL',
      entry_date: e.entry_date ? String(e.entry_date).slice(0, 10) : '',
      period_id: e.period_id ?? '',
      reference: e.reference || '',
      description: e.description || '',
      source_module: e.source_module || '',
      source_id: e.source_id ?? '',
      lines,
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    const { payload, lines } = toJournalPayload(values, Boolean(editing));
    if (lines.length < 2) {
      showSnackbar('At least two lines with amounts are required', 'error');
      return;
    }
    if (!payload.description || payload.description.length < 2) {
      showSnackbar('Description (min 2 chars) is required', 'error');
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        const { lines: entryLines, ...header } = payload;
        await ledgerAPI.updateJournal(editing.id, header);
        await ledgerAPI.updateJournalLines(editing.id, { lines: entryLines });
        showSnackbar('Journal entry updated successfully', 'success');
      } else {
        await ledgerAPI.createJournal(payload);
        showSnackbar('Journal entry created as DRAFT', 'success');
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn, severity = 'info') => {
    confirm({
      title: `${label} journal entry`,
      message: `Are you sure you want to ${label.toLowerCase()} this journal entry?`,
      severity,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Journal entry ${pastTense} successfully`, 'success');
          setDetail(null);
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const submitVoid = async () => {
    if ((voidDlg.reason?.trim() ?? '').length < 10) {
      showSnackbar('Void reason (min 10 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidJournal(voidDlg.entry.id, { void_reason: voidDlg.reason.trim() });
      showSnackbar('Journal entry voided successfully', 'success');
      setVoidDlg({ open: false, entry: null, reason: '' });
      setDetail(null);
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitReverse = async () => {
    try {
      const body = {};
      if (reverseDlg.reversal_date) body.reversal_date = reverseDlg.reversal_date;
      if (reverseDlg.reversal_description?.trim()) body.reversal_description = reverseDlg.reversal_description.trim();
      await ledgerAPI.reverseJournal(reverseDlg.entry.id, body);
      showSnackbar('Journal entry reversed successfully', 'success');
      setReverseDlg({ open: false, entry: null, reversal_date: '', reversal_description: '' });
      setDetail(null);
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitRequest = async () => {
    if ((requestDlg.reason?.trim() ?? '').length < 5) {
      showSnackbar('Reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      const body = { entry_id: requestDlg.entry.id, reason: requestDlg.reason.trim() };
      if (requestDlg.reversal_date) body.reversal_date = requestDlg.reversal_date;
      await ledgerAPI.createReversalRequest(body);
      showSnackbar('Reversal requested - a checker must approve it', 'success');
      setRequestDlg({ open: false, entry: null, reason: '', reversal_date: '' });
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const addLine = async () => {
    const cleaned = cleanLineForPayload(
      { ...newLine, account_id: newLine.account_id, debit_amount: newLine.debit_amount, credit_amount: newLine.credit_amount },
      detailLines.length,
      false
    );
    if (!cleaned.account_id || !(cleaned.debit_amount > 0 || cleaned.credit_amount > 0)) {
      showSnackbar('Account and an amount are required', 'error');
      return;
    }
    try {
      await ledgerAPI.addJournalLine(detail.id, cleaned);
      showSnackbar('Line added successfully', 'success');
      setNewLine(emptyJournalLine(detailLines.length + 2));
      openDetail(detail);
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const removeLine = (line) => {
    confirm({
      title: 'Delete line',
      message: 'Delete this draft line? At least two lines must remain.',
      severity: 'error',
      onConfirm: async () => {
        try {
          await ledgerAPI.deleteJournalLine(detail.id, line.id);
          showSnackbar('Line deleted successfully', 'success');
          openDetail(detail);
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const runBulk = (kind) => {
    if (!selected.length) return;
    confirm({
      title: kind === 'post' ? `Post ${selected.length} draft(s)` : `Delete ${selected.length} draft(s)`,
      message: kind === 'post'
        ? 'Post every selected DRAFT entry? Entries that fail are reported per entry.'
        : 'Delete every selected DRAFT entry? This cannot be undone.',
      severity: kind === 'post' ? 'info' : 'error',
      onConfirm: async () => {
        try {
          const fn = kind === 'post' ? ledgerAPI.bulkPostJournals : ledgerAPI.bulkDeleteJournals;
          const res = await fn({ entry_ids: selected });
          const data = unwrap(res);
          const failed = data.failed ?? data.errors ?? [];
          if (Array.isArray(failed) && failed.length) {
            showSnackbar(`Completed with ${failed.length} failure(s): ${JSON.stringify(failed).slice(0, 200)}`, 'warning');
          } else {
            showSnackbar(`Bulk ${kind} completed successfully`, 'success');
          }
          setSelected([]);
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const loadAccountLedger = async (id, extra = {}) => {
    const accountId = id ?? ledgerDlg.accountId;
    if (!accountId) {
      showSnackbar('Select an account first', 'error');
      return;
    }
    try {
      setLedgerDlg((p) => ({ ...p, loading: true }));
      const params = {
        ...(extra.start_date ?? ledgerDlg.start_date ? { start_date: extra.start_date ?? ledgerDlg.start_date } : {}),
        ...(extra.end_date ?? ledgerDlg.end_date ? { end_date: extra.end_date ?? ledgerDlg.end_date } : {}),
      };
      const res = await ledgerAPI.getAccountLedger(accountId, params);
      // The API returns a single account object (not a paged list):
      // { account_code, account_name, opening_balance, transactions[] (each
      // with entry_number + server-computed running_balance), closing_balance }.
      const payload = unwrap(res);
      setLedgerDlg((p) => ({
        ...p,
        header: {
          code: payload.account_code || '',
          name: payload.account_name || '',
          type: payload.account_type || '',
        },
        rows: payload.transactions ?? [],
        opening: Number(payload.opening_balance) || 0,
        closing: Number(payload.closing_balance) || 0,
        totalDebits: Number(payload.total_debits) || 0,
        totalCredits: Number(payload.total_credits) || 0,
        loading: false,
      }));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch account ledger'), 'error');
      setLedgerDlg((p) => ({ ...p, loading: false }));
    }
  };

  const submitFindByNumber = async () => {
    if (!findDlg.number.trim()) {
      showSnackbar('Enter an entry number', 'error');
      return;
    }
    try {
      setFindDlg((p) => ({ ...p, loading: true }));
      const res = await ledgerAPI.getJournalByNumber(findDlg.number.trim());
      const entry = normalizeJournal(unwrap(res));
      if (!entry || !entry.id) {
        showSnackbar('No journal entry found with that number', 'warning');
        return;
      }
      setFindDlg({ open: false, number: '', loading: false });
      openDetail(entry);
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Entry not found'), 'error');
      setFindDlg((p) => ({ ...p, loading: false }));
    }
  };

  const setFilter = (key) => (e) => {
    const v = e?.target ? e.target.value : e;
    setFilters((p) => ({ ...p, [key]: v }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters(EMPTY_FILTERS);
  };

  const workflowActions = (entry) => {
    const e = normalizeJournal(entry);
    const st = (e.status || '').toUpperCase();
    const buttons = [];
    buttons.push(
      <Tooltip title="View lines" key="view">
        <IconButton size="small" onClick={() => openDetail(e)}>
          <ViewIcon />
        </IconButton>
      </Tooltip>
    );
    if (st === 'DRAFT') {
      if (canUpdate) {
        buttons.push(
          <Tooltip title="Edit draft" key="edit">
            <IconButton size="small" color="primary" onClick={() => openEdit(e)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        );
      }
      if (canPost) {
        buttons.push(
          <Tooltip title="Post" key="post">
            <IconButton size="small" color="primary" onClick={() => runWorkflow('Post', 'posted', () => ledgerAPI.postJournal(e.id))}>
              <PostIcon />
            </IconButton>
          </Tooltip>
        );
      }
      if (canDelete) {
        buttons.push(
          <Tooltip title="Delete draft" key="delete">
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                confirm({
                  title: 'Delete journal entry',
                  message: `Delete journal ${e.entry_number || ''}? Only draft entries can be deleted.`,
                  severity: 'error',
                  onConfirm: async () => {
                    try {
                      await ledgerAPI.deleteJournal(e.id);
                      showSnackbar('Deleted successfully', 'success');
                      load();
                    } catch (error) {
                      showSnackbar(getErrorMessage(error), 'error');
                    }
                  },
                })
              }
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        );
      }
    }
    if ((st === 'POSTED' || st === 'APPROVED') && canApprove) {
      if (st === 'POSTED') {
        buttons.push(
          <Tooltip title="Approve" key="approve">
            <IconButton size="small" color="primary" onClick={() => runWorkflow('Approve', 'approved', () => ledgerAPI.approveJournal(e.id))}>
              <ApproveIcon />
            </IconButton>
          </Tooltip>
        );
      }
      buttons.push(
        <Tooltip title="Void (needs reason)" key="void">
          <IconButton size="small" color="error" onClick={() => setVoidDlg({ open: true, entry: e, reason: '' })}>
            <VoidIcon />
          </IconButton>
        </Tooltip>
      );
      buttons.push(
        <Tooltip title="Reverse now" key="reverse">
          <IconButton size="small" color="warning" onClick={() => setReverseDlg({ open: true, entry: e, reversal_date: '', reversal_description: '' })}>
            <ReverseIcon />
          </IconButton>
        </Tooltip>
      );
    }
    if ((st === 'POSTED' || st === 'APPROVED') && canCreate) {
      buttons.push(
        <Tooltip title="Request reversal (maker-checker)" key="request">
          <IconButton size="small" color="default" onClick={() => setRequestDlg({ open: true, entry: e, reason: '', reversal_date: '' })}>
            <LedgerIcon />
          </IconButton>
        </Tooltip>
      );
    }
    return buttons;
  };

  const draftIds = useMemo(() => items.filter((e) => (e.status || '').toUpperCase() === 'DRAFT').map((e) => e.id), [items]);
  const allDraftSelected = draftIds.length > 0 && draftIds.every((id) => selected.includes(id));
  const someDraftSelected = draftIds.some((id) => selected.includes(id)) && !allDraftSelected;
  const toggleAllDrafts = () => {
    setSelected((prev) => (allDraftSelected ? prev.filter((id) => !draftIds.includes(id)) : [...new Set([...prev, ...draftIds])]));
  };

  const detailTotals = detail ? totalsOf(detail) : { debit: 0, credit: 0 };
  const detailBalanced = Math.abs(detailTotals.debit - detailTotals.credit) < 0.01 && detailTotals.debit > 0;
  const detailPeriodName = detail?.period?.period_name || look.periodLabel(detail?.period_id);
  const detailCreator = personName(detail?.creator);
  const auditRows = detail ? [
    detail.created_at ? ['Created', `${formatDateTime(detail.created_at)}${personName(detail.creator) ? ` by ${personName(detail.creator)}` : ''}`] : null,
    detail.posted_at ? ['Posted', `${formatDateTime(detail.posted_at)}${personName(detail.poster) ? ` by ${personName(detail.poster)}` : ''}`] : null,
    detail.approved_at ? ['Approved', `${formatDateTime(detail.approved_at)}${personName(detail.approver) ? ` by ${personName(detail.approver)}` : ''}`] : null,
    detail.updated_at ? ['Updated', formatDateTime(detail.updated_at)] : null,
  ].filter(Boolean) : [];

  // Expanded line dimensions, resolved to names (rows with no value are
  // hidden). Embedded relation first (cheapest, already on the API
  // response), falling back to the id-based lookup so a field still shows
  // even when the list endpoint didn't eager-load that relation.
  const lineDimensions = (l) => [
    ['Ref', l.reference || ''],
    ['Department', look.departmentLabel(l.department_id)],
    ['Location', look.locationLabel(l.location_id)],
    ['Cost centre', look.costCentreLabel(l.cost_center_id)],
    ['Customer', l.customer?.name || look.customerLabel(l.customer_id)],
    ['Supplier', l.supplier?.name || look.supplierLabel(l.supplier_id)],
    ['Product', l.product ? codeNameOf(l.product, ['item_code'], ['item_name']) : look.productLabel(l.product_id)],
    ['Currency', l.foreign_currency ? codeNameOf(l.foreign_currency, ['currency_code'], ['currency_name']) : look.currencyLabel(l.foreign_currency_id)],
    ['FX rate', l.exchange_rate ? String(l.exchange_rate) : ''],
    ['FX debit', Number(l.foreign_debit) > 0 ? formatAmount(l.foreign_debit) : ''],
    ['FX credit', Number(l.foreign_credit) > 0 ? formatAmount(l.foreign_credit) : ''],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
            {canCreate && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>
                New Journal
              </Button>
            )}
            <Button variant="outlined" size="small" startIcon={<LedgerIcon />} onClick={() => setLedgerDlg({ ...emptyLedgerDlg(), open: true })}>
              Account Ledger
            </Button>
            <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={() => setFindDlg({ open: true, number: '', loading: false })}>
              Find by #
            </Button>
            {selected.length > 0 && (
              <>
                <Button variant="contained" size="small" color="primary" startIcon={<PostIcon />} onClick={() => runBulk('post')}>
                  Post {selected.length}
                </Button>
                <Button variant="outlined" size="small" color="error" startIcon={<DeleteIcon />} onClick={() => runBulk('delete')}>
                  Delete {selected.length}
                </Button>
                <Button size="small" onClick={() => setSelected([])}>
                  Clear
                </Button>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search number, ref, description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small"
              label="Entry #"
              value={filters.entry_number}
              onChange={setFilter('entry_number')}
              sx={{ width: 140 }}
            />
            <Autocomplete
              size="small"
              options={[{ label: 'All types', value: '' }, ...ALL_ENTRY_TYPES.map((s) => ({ label: s, value: s }))]}
              getOptionLabel={(o) => o.label}
              value={[{ label: 'All types', value: '' }, ...ALL_ENTRY_TYPES.map((s) => ({ label: s, value: s }))].find((o) => o.value === filters.entry_type)}
              onChange={(_, o) => setFilters((p) => ({ ...p, entry_type: o?.value || '' }))}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(params) => <TextField {...params} label="Type" />}
              sx={{ minWidth: 150 }}
            />
            <Autocomplete
              size="small"
              options={[{ label: 'All statuses', value: '' }, ...ENTRY_STATUSES.map((s) => ({ label: s, value: s }))]}
              getOptionLabel={(o) => o.label}
              value={[{ label: 'All statuses', value: '' }, ...ENTRY_STATUSES.map((s) => ({ label: s, value: s }))].find((o) => o.value === filters.status)}
              onChange={(_, o) => setFilters((p) => ({ ...p, status: o?.value || '' }))}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(params) => <TextField {...params} label="Status" />}
              sx={{ minWidth: 140 }}
            />
            <Autocomplete
              size="small"
              options={[{ label: 'All sources', value: '' }, ...SOURCE_MODULES.map((s) => ({ label: s, value: s }))]}
              getOptionLabel={(o) => o.label}
              value={[{ label: 'All sources', value: '' }, ...SOURCE_MODULES.map((s) => ({ label: s, value: s }))].find((o) => o.value === filters.source_module)}
              onChange={(_, o) => setFilters((p) => ({ ...p, source_module: o?.value || '' }))}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(params) => <TextField {...params} label="Source" />}
              sx={{ minWidth: 160 }}
            />
            <Box sx={{ minWidth: 200 }}>
              <FiscalPeriodPicker value={filters.period_id} onChange={(id) => setFilters((p) => ({ ...p, period_id: id }))} />
            </Box>
            <Box sx={{ minWidth: 220 }}>
              <AccountPicker value={filters.account_id} onChange={(id) => setFilters((p) => ({ ...p, account_id: id }))} />
            </Box>
            <TextField size="small" type="date" label="From" value={filters.start_date} onChange={setFilter('start_date')} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="To" value={filters.end_date} onChange={setFilter('end_date')} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="number" placeholder="Min amount" value={filters.min_amount} onChange={setFilter('min_amount')} sx={{ width: 120 }} />
            <TextField size="small" type="number" placeholder="Max amount" value={filters.max_amount} onChange={setFilter('max_amount')} sx={{ width: 120 }} />
            {hasActiveFilters && (
              <Button variant="outlined" size="small" startIcon={<ClearFiltersIcon />} onClick={clearFilters}>
                Clear
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {fetching && <LinearProgress />}
          <Box sx={{ opacity: fetching ? 0.5 : 1, transition: 'opacity 0.2s', p: 2 }}>
            {items.length === 0 && !loading ? (
              <EmptyRecordsState message="No journal entries found." />
            ) : (
              <DataTable
                columns={['', 'Entry', 'Type', 'Date', 'Description', 'Debit', 'Credit', 'Status', 'Actions']}
                data={items}
                loading={loading}
                emptyMessage="No journal entries found."
                minWidth={900}
                renderRow={(entry, index) => {
                  const e = normalizeJournal(entry);
                  const t = totalsOf(e);
                  const isDraft = (e.status || '').toUpperCase() === 'DRAFT';
                  const creator = personName(e.creator);
                  return (
                    <TableRow
                      key={e.id}
                      hover
                      selected={selected.includes(e.id)}
                      onClick={() => openDetail(e)}
                      sx={{ cursor: 'pointer', backgroundColor: index % 2 !== 0 ? 'action.hover' : 'background.paper' }}
                    >
                      <TableCell padding="checkbox" onClick={(ev) => ev.stopPropagation()}>
                        {isDraft ? (
                          <Checkbox
                            size="small"
                            checked={selected.includes(e.id)}
                            onChange={() => setSelected((prev) => (prev.includes(e.id) ? prev.filter((id) => id !== e.id) : [...prev, e.id]))}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {e.entry_number || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {e.reference || (creator ? `By ${creator}` : '') || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>Type</Typography>
                        <Typography variant="body2" color="text.secondary">{e.entry_type || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatDateTime(e.entry_date)}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>Date</Typography>
                            <Typography variant="body2" color="text.secondary">{formatDate(e.entry_date)}</Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>Description</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                          {e.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>Debit</Typography>
                        <Typography variant="body2" color="text.secondary">{formatAmount(t.debit)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>Credit</Typography>
                        <Typography variant="body2" color="text.secondary">{formatAmount(t.credit)}</Typography>
                      </TableCell>
                      <TableCell onClick={(ev) => ev.stopPropagation()}>
                        <JournalStatusChip status={e.status} />
                      </TableCell>
                      <TableCell onClick={(ev) => ev.stopPropagation()}>
                        <JournalRowActionsMenu
                          entry={e}
                          canUpdate={canUpdate} canPost={canPost} canApprove={canApprove} canCreate={canCreate} canDelete={canDelete}
                          onView={() => openDetail(e)}
                          onEdit={() => openEdit(e)}
                          onPost={() => runWorkflow('Post', 'posted', () => ledgerAPI.postJournal(e.id))}
                          onDelete={() =>
                            confirm({
                              title: 'Delete journal entry',
                              message: `Delete journal ${e.entry_number || ''}? Only draft entries can be deleted.`,
                              severity: 'error',
                              onConfirm: async () => {
                                try {
                                  await ledgerAPI.deleteJournal(e.id);
                                  showSnackbar('Deleted successfully', 'success');
                                  load();
                                } catch (error) {
                                  showSnackbar(getErrorMessage(error), 'error');
                                }
                              },
                            })
                          }
                          onApprove={() => runWorkflow('Approve', 'approved', () => ledgerAPI.approveJournal(e.id))}
                          onVoid={() => setVoidDlg({ open: true, entry: e, reason: '' })}
                          onReverse={() => setReverseDlg({ open: true, entry: e, reversal_date: '', reversal_description: '' })}
                          onRequestReversal={() => setRequestDlg({ open: true, entry: e, reason: '', reversal_date: '' })}
                        />
                      </TableCell>
                    </TableRow>
                  );
                }}
              />
            )}
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Checkbox size="small" checked={allDraftSelected} indeterminate={someDraftSelected} onChange={toggleAllDrafts} disabled={!draftIds.length} />
              <Typography variant="caption" color="text.secondary">
                {selected.length ? `${selected.length} selected (drafts only)` : 'Select drafts on this page'}
              </Typography>
              <TablePagination
                pagination={{ current_page: page, last_page: Math.max(1, Math.ceil(total / size)), per_page: size, total }}
                onPageChange={(p) => setPage(p)}
                onPageSizeChange={(s) => { setSize(parseInt(s, 10)); setPage(1); }}
                isMobile={false}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <JournalFormDialog
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? 'Edit Draft Journal' : 'New Journal Entry'}
        values={values}
        isEdit={Boolean(editing)}
        allowedTypes={editing ? [...new Set([...CREATABLE_ENTRY_TYPES, values.entry_type])] : undefined}
        onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
        onSubmit={handleSubmit}
        saving={saving}
      />

      <Dialog open={!!detail} onClose={noBackdrop(() => setDetail(null))} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6">Journal {detail?.entry_number || ''}</Typography>
              <JournalStatusChip status={detail?.status} />
              {detail && (
                detailBalanced ? (
                  <Chip label="Balanced" color="success" size="small" variant="outlined" />
                ) : (
                  <Chip label="Unbalanced" color="error" size="small" />
                )
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {detailCreator ? `By ${detailCreator}` : ''} {detail?.entry_date ? `· ${formatDate(detail.entry_date)}` : ''}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Basic information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 1.5 }}>
                <DetailField label="Entry number">{detail.entry_number || '-'}</DetailField>
                <DetailField label="Entry type">{detail.entry_type || '-'}</DetailField>
                <DetailField label="Entry date">{formatDate(detail.entry_date)}</DetailField>
                <DetailField label="Fiscal period">{detailPeriodName || '-'}</DetailField>
                <DetailField label="Reference">{detail.reference || '-'}</DetailField>
                <DetailField label="Source">
                  {detail.source_module ? `${detail.source_module}${detail.source_id ? ` #${detail.source_id}` : ''}` : 'MANUAL'}
                </DetailField>
                <DetailField label="Reversing">{detail.is_reversing ? 'Yes' : 'No'}</DetailField>
                <DetailField label="Total debit / credit">
                  {formatAmount(detailTotals.debit)} / {formatAmount(detailTotals.credit)}
                </DetailField>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <DetailField label="Description">{detail.description || '-'}</DetailField>
                </Box>
              </Box>
            </Box>
          )}
          {detailLoading ? (
            <LinearProgress />
          ) : detailLines.length === 0 ? (
            <Typography color="text.secondary">No lines found.</Typography>
          ) : (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Line items
              </Typography>
              <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'success.50', fontWeight: 700 }}>Debit</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'error.50', fontWeight: 700 }}>Credit</TableCell>
                  <TableCell />
                  {detail?.status === 'DRAFT' && canUpdate && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {detailLines.map((l, i) => {
                  const dims = lineDimensions(l);
                  const open = expandedLines.has(i);
                  return (
                    <React.Fragment key={l.id ?? i}>
                      <TableRow hover>
                        <TableCell>{l.line_number ?? i + 1}</TableCell>
                        <TableCell><strong>{lineAccountLabel(l, look) || '-'}</strong></TableCell>
                        <TableCell>{l.description || '-'}</TableCell>
                        <TableCell align="right"><strong>{Number(l.debit_amount || 0) > 0 ? formatAmount(l.debit_amount) : '-'}</strong></TableCell>
                        <TableCell align="right"><strong>{Number(l.credit_amount || 0) > 0 ? formatAmount(l.credit_amount) : '-'}</strong></TableCell>
                        <TableCell>
                          {dims.length > 0 && (
                            <IconButton size="small" onClick={() => setExpandedLines((p) => { const n = new Set(p); if (n.has(i)) n.delete(i); else n.add(i); return n; })}>
                              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        {detail?.status === 'DRAFT' && canUpdate && (
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => removeLine(l)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                      {dims.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                            <Collapse in={open}>
                              <Box sx={{ p: 1.5, bgcolor: 'grey.50', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {dims.map(([k, v]) => (
                                  <Box key={k}>
                                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                                    <Typography variant="body2" fontWeight={600}>{String(v)}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
              </Table>
            </Box>
          )}
          {auditRows.length > 0 && (
            <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Audit
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 1 }}>
                {auditRows.map(([k, v]) => (
                  <DetailField key={k} label={k}>{v}</DetailField>
                ))}
              </Box>
            </Box>
          )}
          {detail?.status === 'DRAFT' && canUpdate && (
            <Box mt={2}>
              <Typography variant="subtitle2" fontWeight={700}>Add line</Typography>
              <Grid container spacing={1} mt={0.5} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <AccountPicker value={newLine.account_id} onChange={(id) => setNewLine((p) => ({ ...p, account_id: id }))} />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField fullWidth size="small" type="number" label="Debit" value={newLine.debit_amount} onChange={(e) => setNewLine((p) => ({ ...p, debit_amount: e.target.value }))} />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField fullWidth size="small" type="number" label="Credit" value={newLine.credit_amount} onChange={(e) => setNewLine((p) => ({ ...p, credit_amount: e.target.value }))} />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth size="small" label="Description" value={newLine.description} onChange={(e) => setNewLine((p) => ({ ...p, description: e.target.value }))} />
                </Grid>
                <Grid size={{ xs: 12, md: 1 }}>
                  <Button fullWidth size="small" variant="outlined" onClick={addLine}>Add</Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
          {detail && workflowActions(detail).slice(1).map((b, i) => (
            <span key={i}>{b}</span>
          ))}
        </DialogActions>
      </Dialog>

      <Dialog open={voidDlg.open} onClose={noBackdrop(() => setVoidDlg({ open: false, entry: null, reason: '' }))} maxWidth="xs" fullWidth>
        <DialogTitle>Void journal {voidDlg.entry?.entry_number}</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} size="small" label="Void reason (min 10 chars)" value={voidDlg.reason} onChange={(e) => setVoidDlg((p) => ({ ...p, reason: e.target.value }))} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDlg({ open: false, entry: null, reason: '' })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitVoid}>Void</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reverseDlg.open} onClose={noBackdrop(() => setReverseDlg({ open: false, entry: null, reversal_date: '', reversal_description: '' }))} maxWidth="xs" fullWidth>
        <DialogTitle>Reverse journal {reverseDlg.entry?.entry_number}</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" type="date" label="Reversal date" value={reverseDlg.reversal_date} onChange={(e) => setReverseDlg((p) => ({ ...p, reversal_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mt: 1 }} />
          <TextField fullWidth size="small" label="Description" value={reverseDlg.reversal_description} onChange={(e) => setReverseDlg((p) => ({ ...p, reversal_description: e.target.value }))} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseDlg({ open: false, entry: null, reversal_date: '', reversal_description: '' })}>Cancel</Button>
          <Button variant="contained" onClick={submitReverse}>Reverse</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={requestDlg.open} onClose={noBackdrop(() => setRequestDlg({ open: false, entry: null, reason: '', reversal_date: '' }))} maxWidth="xs" fullWidth>
        <DialogTitle>Request reversal of {requestDlg.entry?.entry_number}</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} size="small" label="Reason (min 5 chars)" value={requestDlg.reason} onChange={(e) => setRequestDlg((p) => ({ ...p, reason: e.target.value }))} sx={{ mt: 1 }} />
          <TextField fullWidth size="small" type="date" label="Reversal date (optional)" value={requestDlg.reversal_date} onChange={(e) => setRequestDlg((p) => ({ ...p, reversal_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mt: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            A checker (approver) must approve the request - makers cannot reverse their own entries this way.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDlg({ open: false, entry: null, reason: '', reversal_date: '' })}>Cancel</Button>
          <Button variant="contained" onClick={submitRequest}>Submit request</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ledgerDlg.open} onClose={noBackdrop(() => setLedgerDlg((p) => ({ ...p, open: false })))} maxWidth="lg" fullWidth>
        <DialogTitle>
          Account ledger{ledgerDlg.header ? ` — ${[ledgerDlg.header.code, ledgerDlg.header.name].filter(Boolean).join(' - ')}` : ''}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1} mb={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <AccountPicker
                value={ledgerDlg.accountId}
                onChange={(id) => {
                  setLedgerDlg((p) => ({ ...emptyLedgerDlg(), open: true, accountId: id, start_date: p.start_date, end_date: p.end_date }));
                  if (id) loadAccountLedger(id);
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="From" value={ledgerDlg.start_date} onChange={(e) => setLedgerDlg((p) => ({ ...p, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="To" value={ledgerDlg.end_date} onChange={(e) => setLedgerDlg((p) => ({ ...p, end_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
          {ledgerDlg.accountId && (
            <Box mb={1} display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Button size="small" variant="outlined" onClick={() => loadAccountLedger()}>Reload</Button>
              {ledgerDlg.header?.type && (
                <Typography variant="caption" color="text.secondary">{ledgerDlg.header.type}</Typography>
              )}
            </Box>
          )}
          {ledgerDlg.loading ? (
            <LinearProgress />
          ) : !ledgerDlg.accountId ? (
            <Typography color="text.secondary">Select an account to load its running balance.</Typography>
          ) : ledgerDlg.rows.length === 0 ? (
            <Typography color="text.secondary">No posted entries for this account in the selected period.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Entry</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5}><em>Opening balance</em></TableCell>
                  <TableCell align="right"><strong>{formatAmount(ledgerDlg.opening)}</strong></TableCell>
                </TableRow>
                {ledgerDlg.rows.map((r, i) => (
                  <TableRow key={r.entry_id ?? i} hover>
                    <TableCell>{formatDate(r.entry_date)}</TableCell>
                    <TableCell><strong>{r.entry_number || '-'}</strong></TableCell>
                    <TableCell>{r.description || r.reference || '-'}</TableCell>
                    <TableCell align="right">{Number(r.debit_amount) > 0 ? formatAmount(r.debit_amount) : '-'}</TableCell>
                    <TableCell align="right">{Number(r.credit_amount) > 0 ? formatAmount(r.credit_amount) : '-'}</TableCell>
                    <TableCell align="right">{formatAmount(r.running_balance ?? 0)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right"><strong>Totals</strong></TableCell>
                  <TableCell align="right"><strong>{formatAmount(ledgerDlg.totalDebits)}</strong></TableCell>
                  <TableCell align="right"><strong>{formatAmount(ledgerDlg.totalCredits)}</strong></TableCell>
                  <TableCell align="right"><strong>{formatAmount(ledgerDlg.closing)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLedgerDlg((p) => ({ ...p, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={findDlg.open} onClose={noBackdrop(() => setFindDlg({ open: false, number: '', loading: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Find journal by number</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Entry number (e.g. JE-000123)"
            value={findDlg.number}
            onChange={(e) => setFindDlg((p) => ({ ...p, number: e.target.value }))}
            sx={{ mt: 1 }}
            onKeyDown={(e) => { if (e.key === 'Enter') submitFindByNumber(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFindDlg({ open: false, number: '', loading: false })}>Cancel</Button>
          <Button variant="contained" onClick={submitFindByNumber} disabled={findDlg.loading || !findDlg.number.trim()}>
            {findDlg.loading ? 'Searching...' : 'Open'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Reversal requests tab (maker-checker)
// ---------------------------------------------------------------------------

const ReversalRequestsTab = ({ can, showSnackbar, confirm }) => {
  const resource = RESOURCES.LEDGER.GL;
  const canCreate = can(resource, ACTIONS.CREATE);
  const canApprove = can(resource, ACTIONS.APPROVE);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [entryId, setEntryId] = useState('');
  const [review, setReview] = useState({ open: false, request: null, mode: null, notes: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (status) params.status = status;
      // entry_id is numeric server-side; free text is filtered client-side by
      // entry number below, so only forward pure numbers to the API.
      if (entryId && /^\d+$/.test(entryId.trim())) params.entry_id = Number(entryId.trim());
      const res = await ledgerAPI.getReversalRequests(params);
      const payload = unwrap(res);
      const list = Array.isArray(payload) ? payload : payload.items ?? [];
      setItems(list.map((r) => ({ ...r, id: getLedgerId(r, ['reversal_request_id', 'request_id']) })));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch reversal requests'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, entryId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitReview = async () => {
    const { request, mode, notes } = review;
    try {
      if (mode === 'approve') {
        await ledgerAPI.approveReversalRequest(request.id, notes.trim() ? { review_notes: notes.trim() } : {});
        showSnackbar('Reversal approved and posted', 'success');
      } else if (mode === 'reject') {
        await ledgerAPI.rejectReversalRequest(request.id, notes.trim() ? { review_notes: notes.trim() } : {});
        showSnackbar('Reversal request rejected', 'success');
      } else {
        await ledgerAPI.cancelReversalRequest(request.id);
        showSnackbar('Reversal request cancelled', 'success');
      }
      setReview({ open: false, request: null, mode: null, notes: '' });
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                size="small"
                options={['', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'].map((s) => ({ label: s || 'All statuses', value: s }))}
                getOptionLabel={(o) => o.label}
                value={['', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'].map((s) => ({ label: s || 'All statuses', value: s })).find((o) => o.value === status)}
                onChange={(_, o) => setStatus(o?.value || '')}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                renderInput={(params) => <TextField {...params} label="Status" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Entry number contains"
                placeholder="e.g. JE-2026"
                value={entryId}
                onChange={(e) => setEntryId(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Makers request reversals from the Entries tab. Checkers approve or
                reject - the approver cannot be the requester.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {items.length === 0 && !loading ? (
            <EmptyRecordsState message="No reversal requests found." />
          ) : (
            <DataTable
              columns={['Request', 'Entry', 'Reason', 'Reversal date', 'Status', 'Actions']}
              data={items.filter((r) => !entryId.trim() || (r.entry?.entry_number || r.entry_number || '').toLowerCase().includes(entryId.trim().toLowerCase()))}
              loading={loading}
              emptyMessage="No reversal requests found."
              renderRow={(r) => {
                const st = (r.status || '').toUpperCase();
                return (
                  <TableRow key={r.id} hover>
                    <TableCell><strong>#{r.id}</strong></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.entry?.entry_number || r.entry_number || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.requested_at ? `Requested ${formatDate(r.requested_at)}` : ''}
                        {r.reviewed_at ? ` · Reviewed ${formatDate(r.reviewed_at)}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{r.reason || '-'}</Typography>
                      {r.review_notes && <Typography variant="caption" color="text.secondary">Review: {r.review_notes}</Typography>}
                    </TableCell>
                    <TableCell>{formatDate(r.reversal_date)}</TableCell>
                    <TableCell><JournalStatusChip status={r.status} /></TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {canApprove && st === 'PENDING' && (
                          <>
                            <Tooltip title="Approve and post reversal">
                              <IconButton size="small" color="success" onClick={() => setReview({ open: true, request: r, mode: 'approve', notes: '' })}>
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton size="small" color="error" onClick={() => setReview({ open: true, request: r, mode: 'reject', notes: '' })}>
                                <VoidIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {canCreate && st === 'PENDING' && (
                          <Tooltip title="Cancel my request">
                            <IconButton size="small" onClick={() => setReview({ open: true, request: r, mode: 'cancel', notes: '' })}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }}
            />
          )}
        </CardContent>
      </Card>
      <Dialog open={review.open} onClose={noBackdrop(() => setReview({ open: false, request: null, mode: null, notes: '' }))} maxWidth="xs" fullWidth>
        <DialogTitle>
          {review.mode === 'approve' ? 'Approve reversal' : review.mode === 'reject' ? 'Reject reversal' : 'Cancel request'} #{review.request?.id}
        </DialogTitle>
        <DialogContent>
          {review.mode !== 'cancel' && (
            <TextField fullWidth multiline rows={3} size="small" label="Review notes (optional)" value={review.notes} onChange={(e) => setReview((p) => ({ ...p, notes: e.target.value }))} sx={{ mt: 1 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReview({ open: false, request: null, mode: null, notes: '' })}>Close</Button>
          <Button variant="contained" color={review.mode === 'approve' ? 'success' : review.mode === 'reject' ? 'error' : 'primary'} onClick={submitReview}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Recurring journals tab
// ---------------------------------------------------------------------------

// RecurringJournalLineDto (accounting-apis) only accepts these five on top
// of account/amounts/description — no reference, no product, no FX. Product
// and FX fields exist on regular journal lines but not on the recurring
// template's own schema, so they're deliberately absent from this list.
const RECURRING_EXTRA_FIELD_DEFS = [
  { key: 'department_id', label: 'Department' },
  { key: 'location_id', label: 'Location' },
  { key: 'cost_center_id', label: 'Cost centre' },
  { key: 'customer_id', label: 'Customer' },
  { key: 'supplier_id', label: 'Supplier' },
];
const RECURRING_EXTRA_FIELD_KEYS = RECURRING_EXTRA_FIELD_DEFS.map((f) => f.key);

const RecurringTab = ({ can, showSnackbar, confirm }) => {
  const resource = RESOURCES.LEDGER.GL;
  const canCreate = can(resource, ACTIONS.CREATE);
  const canUpdate = can(resource, ACTIONS.UPDATE);
  const canDelete = can(resource, ACTIONS.DELETE);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ template_name: '', description: '', reference: '', entry_type: 'GENERAL', interval_months: 1, start_date: '', end_date: '', lines: [{ account_id: '', debit_amount: 0, credit_amount: 0, description: '' }] });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ open: false, rows: [] });
  const [focusedAmount, setFocusedAmount] = useState(null); // { index, key } | null
  const [manualExtras, setManualExtras] = useState({});
  const [addMenu, setAddMenu] = useState(null); // { index, anchorEl } | null
  const look = useLedgerLookups();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ledgerAPI.getRecurringJournals({ limit: 100 });
      const payload = unwrap(res);
      const list = Array.isArray(payload) ? payload : payload.items ?? [];
      setItems(list.map((r) => ({ ...r, id: getLedgerId(r, ['recurring_journal_id', 'template_id']) })));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch recurring journals'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setDialog({ open: true, editing: null });
    setForm({ template_name: '', description: '', reference: '', entry_type: 'GENERAL', interval_months: 1, start_date: '', end_date: '', lines: [{ account_id: '', debit_amount: 0, credit_amount: 0, description: '' }] });
    setManualExtras({});
  };

  const openEdit = (r) => {
    setDialog({ open: true, editing: r });
    setForm({
      template_name: r.template_name || '',
      description: r.description || '',
      reference: r.reference || '',
      entry_type: r.entry_type || 'GENERAL',
      interval_months: r.interval_months ?? 1,
      start_date: r.start_date ? String(r.start_date).slice(0, 10) : '',
      end_date: r.end_date ? String(r.end_date).slice(0, 10) : '',
      lines: (r.lines || []).map((l) => ({
        account_id: l.account_id ?? '',
        debit_amount: Number(l.debit_amount ?? 0) || 0,
        credit_amount: Number(l.credit_amount ?? 0) || 0,
        description: l.description || '',
        department_id: l.department_id ?? '',
        location_id: l.location_id ?? '',
        cost_center_id: l.cost_center_id ?? '',
        customer_id: l.customer_id ?? '',
        supplier_id: l.supplier_id ?? '',
      })),
    });
    setManualExtras({});
  };

  const setLine = (i, key, value) => {
    setForm((p) => ({ ...p, lines: p.lines.map((l, j) => (j === i ? { ...l, [key]: value } : l)) }));
  };

  const activeExtraFieldsFor = (line, index) => {
    const manual = manualExtras[index];
    return RECURRING_EXTRA_FIELD_KEYS.filter((k) => (manual && manual.has(k)) || Boolean(line[k]));
  };

  const addExtraField = (index, key) => {
    setManualExtras((prev) => {
      const next = { ...prev };
      next[index] = new Set(next[index] || []);
      next[index].add(key);
      return next;
    });
  };

  const removeExtraField = (index, key) => {
    setLine(index, key, '');
    setManualExtras((prev) => {
      const next = { ...prev };
      const set = new Set(next[index] || []);
      set.delete(key);
      next[index] = set;
      return next;
    });
  };

  const removeRecurringLine = (index) => {
    setForm((p) => ({ ...p, lines: p.lines.filter((_, j) => j !== index) }));
    setManualExtras((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, set]) => {
        const v = Number(k);
        if (v < index) next[v] = set;
        else if (v > index) next[v - 1] = set;
      });
      return next;
    });
  };

  const renderRecurringExtraField = (line, i, key) => {
    const def = RECURRING_EXTRA_FIELD_DEFS.find((f) => f.key === key);
    const field = (() => {
      switch (key) {
        case 'department_id':
          return <DepartmentPicker value={line.department_id || ''} onChange={(id) => setLine(i, 'department_id', id)} />;
        case 'location_id':
          return <LocationPicker value={line.location_id || ''} onChange={(id) => setLine(i, 'location_id', id)} />;
        case 'cost_center_id':
          return <CostCentrePicker value={line.cost_center_id || ''} onChange={(id) => setLine(i, 'cost_center_id', id)} />;
        case 'customer_id':
          return <CustomerPicker value={line.customer_id || ''} onChange={(id) => setLine(i, 'customer_id', id)} />;
        case 'supplier_id':
          return <SupplierPicker value={line.supplier_id || ''} onChange={(id) => setLine(i, 'supplier_id', id)} />;
        default:
          return null;
      }
    })();
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 170 }}>
        <Box sx={{ flex: 1 }}>{field}</Box>
        <IconButton size="small" onClick={() => removeExtraField(i, key)} title={`Remove ${def.label}`} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  };

  const templateTotalDebits = form.lines.reduce((sum, l) => sum + (Number(l.debit_amount) || 0), 0);
  const templateTotalCredits = form.lines.reduce((sum, l) => sum + (Number(l.credit_amount) || 0), 0);
  const templateBalanced = Math.abs(templateTotalDebits - templateTotalCredits) < 0.01 && templateTotalDebits > 0;

  const submit = async () => {
    const lines = form.lines
      .filter((l) => l.account_id && (Number(l.debit_amount) > 0 || Number(l.credit_amount) > 0))
      .map((l) => {
        const line = {
          account_id: Number(l.account_id),
          debit_amount: Number(l.debit_amount) || 0,
          credit_amount: Number(l.credit_amount) || 0,
          description: l.description || undefined,
        };
        RECURRING_EXTRA_FIELD_KEYS.forEach((k) => {
          if (l[k]) line[k] = Number(l[k]);
        });
        return line;
      });
    if (!form.template_name.trim() || !form.start_date || lines.length < 2) {
      showSnackbar('Name, start date and at least two lines with amounts are required', 'error');
      return;
    }
    if (!templateBalanced) {
      showSnackbar('Lines must balance (debits must equal credits)', 'error');
      return;
    }
    const body = {
      template_name: form.template_name.trim(),
      start_date: form.start_date,
      lines,
      interval_months: Number(form.interval_months) || 1,
      entry_type: form.entry_type,
    };
    if (form.description.trim()) body.description = form.description.trim();
    if (form.reference.trim()) body.reference = form.reference.trim();
    if (form.end_date) body.end_date = form.end_date;
    try {
      setSaving(true);
      if (dialog.editing) {
        await ledgerAPI.updateRecurringJournal(dialog.editing.id, body);
        showSnackbar('Recurring journal updated successfully', 'success');
      } else {
        await ledgerAPI.createRecurringJournal(body);
        showSnackbar('Recurring journal created successfully', 'success');
      }
      setDialog({ open: false, editing: null });
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async () => {
    try {
      const res = await ledgerAPI.previewRecurringJournals({});
      const payload = unwrap(res);
      setPreview({ open: true, rows: Array.isArray(payload) ? payload : payload.items ?? [] });
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const doRun = () => {
    confirm({
      title: 'Generate due entries',
      message: 'Generate one DRAFT entry per due recurring template?',
      onConfirm: async () => {
        try {
          await ledgerAPI.runRecurringJournals({});
          showSnackbar('Due recurring entries generated as DRAFT', 'success');
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const togglePause = (r) => {
    const paused = (r.status || '').toUpperCase() === 'PAUSED';
    confirm({
      title: paused ? 'Resume template' : 'Pause template',
      message: `Are you sure you want to ${paused ? 'resume' : 'pause'} "${r.template_name}"?`,
      onConfirm: async () => {
        try {
          if (paused) await ledgerAPI.resumeRecurringJournal(r.id);
          else await ledgerAPI.pauseRecurringJournal(r.id);
          showSnackbar(`Template ${paused ? 'resumed' : 'paused'} successfully`, 'success');
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={1} flexWrap="wrap">
            {canCreate && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>
                New Recurring Template
              </Button>
            )}
            <Button variant="outlined" size="small" onClick={doPreview}>Preview due</Button>
            {canUpdate && (
              <Button variant="outlined" size="small" startIcon={<RunIcon />} onClick={doRun}>
                Generate due entries
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {items.length === 0 && !loading ? (
            <EmptyRecordsState message="No recurring journals found." />
          ) : (
            <DataTable
              columns={['Template', 'Every (months)', 'Start', 'Next due', 'End', 'Status', 'Actions']}
              data={items}
              loading={loading}
              emptyMessage="No recurring journals found."
              renderRow={(r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.template_name || r.template_number || 'Recurring template'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[r.template_number, r.description].filter(Boolean).join(' · ') || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.interval_months ?? 1}</TableCell>
                  <TableCell>{r.start_date ? formatDate(r.start_date) : '-'}</TableCell>
                  <TableCell>{r.next_due_date ? formatDate(r.next_due_date) : '-'}</TableCell>
                  <TableCell>{r.end_date ? formatDate(r.end_date) : '-'}</TableCell>
                  <TableCell><JournalStatusChip status={r.status || 'ACTIVE'} /></TableCell>
                  <TableCell>
                    <RecurringRowActionsMenu
                      template={r}
                      canUpdate={canUpdate}
                      canDelete={canDelete}
                      onEdit={() => openEdit(r)}
                      onTogglePause={() => togglePause(r)}
                      onDelete={() =>
                        confirm({
                          title: 'Delete template',
                          message: `Delete "${r.template_name}"? Only templates that never generated entries can be deleted.`,
                          severity: 'error',
                          onConfirm: async () => {
                            try {
                              await ledgerAPI.deleteRecurringJournal(r.id);
                              showSnackbar('Deleted successfully', 'success');
                              load();
                            } catch (error) {
                              showSnackbar(getErrorMessage(error), 'error');
                            }
                          },
                        })
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onClose={noBackdrop(() => setDialog({ open: false, editing: null }))} maxWidth="lg" fullWidth>
        <DialogTitle>{dialog.editing ? 'Edit Recurring Template' : 'New Recurring Template'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" label="Template name" value={form.template_name}
                onChange={(e) => setForm((p) => ({ ...p, template_name: e.target.value }))}
                onKeyDown={handleGridKeyDown} required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" select label="Entry type" value={form.entry_type}
                onChange={(e) => setForm((p) => ({ ...p, entry_type: e.target.value }))} SelectProps={{ native: true }}
              >
                {CREATABLE_ENTRY_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" type="number" label="Every (months)" value={form.interval_months}
                onChange={(e) => setForm((p) => ({ ...p, interval_months: e.target.value }))}
                onWheel={stopWheelChange} onKeyDown={handleGridKeyDown}
                inputProps={{ min: 1 }} sx={noSpinnerSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Reference" value={form.reference} onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))} onKeyDown={handleGridKeyDown} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="date" label="Start date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="date" label="End date (optional)" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} onKeyDown={handleGridKeyDown} />
            </Grid>
          </Grid>

          <Box
            mt={3} display="flex" alignItems="center" flexWrap="wrap" gap={1.5}
            sx={{ position: 'sticky', top: 0, zIndex: 1, py: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="subtitle1" fontWeight={700}>Lines ({form.lines.length}, minimum 2, must balance)</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Dr {templateTotalDebits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cr {templateTotalCredits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
              {templateBalanced ? (
                <Chip label="Balanced" color="success" size="small" variant="outlined" />
              ) : (
                <Chip
                  label={`Out by ${Math.abs(templateTotalDebits - templateTotalCredits).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  color="error" size="small" variant="outlined"
                />
              )}
            </Box>
          </Box>
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'success.50', fontWeight: 700 }}>Debit</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'error.50', fontWeight: 700 }}>Credit</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.lines.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <AccountPicker value={l.account_id} onChange={(id) => setLine(i, 'account_id', id)} sx={{ minWidth: 260 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'success.50' }}>
                      <TextField
                        size="small" type="text" inputMode="decimal"
                        value={
                          focusedAmount?.index === i && focusedAmount?.key === 'debit_amount'
                            ? String(l.debit_amount ?? '')
                            : formatAmount(l.debit_amount)
                        }
                        onChange={(e) => {
                          const raw = parseAmountInput(e.target.value);
                          if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                          setLine(i, 'debit_amount', raw);
                        }}
                        onFocus={(e) => { setFocusedAmount({ index: i, key: 'debit_amount' }); e.target.select(); }}
                        onBlur={() => setFocusedAmount(null)}
                        onKeyDown={handleGridKeyDown}
                        sx={{ width: 170, bgcolor: 'background.paper' }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'error.50' }}>
                      <TextField
                        size="small" type="text" inputMode="decimal"
                        value={
                          focusedAmount?.index === i && focusedAmount?.key === 'credit_amount'
                            ? String(l.credit_amount ?? '')
                            : formatAmount(l.credit_amount)
                        }
                        onChange={(e) => {
                          const raw = parseAmountInput(e.target.value);
                          if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                          setLine(i, 'credit_amount', raw);
                        }}
                        onFocus={(e) => { setFocusedAmount({ index: i, key: 'credit_amount' }); e.target.select(); }}
                        onBlur={() => setFocusedAmount(null)}
                        onKeyDown={handleGridKeyDown}
                        sx={{ width: 170, bgcolor: 'background.paper' }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={l.description || ''} onChange={(e) => setLine(i, 'description', e.target.value)} onKeyDown={handleGridKeyDown} sx={{ minWidth: 180 }} />
                    </TableCell>
                    {/* Same "add on demand, trail off in this row" pattern
                        as the journal entry form — only the five dimension
                        fields the recurring line schema actually accepts. */}
                    {activeExtraFieldsFor(l, i).map((key) => (
                      <TableCell key={key} sx={{ p: 1 }}>{renderRecurringExtraField(l, i, key)}</TableCell>
                    ))}
                    <TableCell sx={{ p: 1 }}>
                      <IconButton size="small" onClick={(e) => setAddMenu({ index: i, anchorEl: e.currentTarget })} title="Add detail">
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" disabled={form.lines.length <= 1} onClick={() => removeRecurringLine(i)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Menu anchorEl={addMenu?.anchorEl} open={Boolean(addMenu)} onClose={() => setAddMenu(null)}>
            {addMenu &&
              RECURRING_EXTRA_FIELD_DEFS.filter((f) => !activeExtraFieldsFor(form.lines[addMenu.index], addMenu.index).includes(f.key)).map((f) => (
                <MenuItem
                  key={f.key}
                  onClick={() => {
                    addExtraField(addMenu.index, f.key);
                    setAddMenu(null);
                  }}
                >
                  {f.label}
                </MenuItem>
              ))}
          </Menu>
          <Box mt={1}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setForm((p) => ({ ...p, lines: [...p.lines, { account_id: '', debit_amount: 0, credit_amount: 0, description: '' }] }))}>
              Add line
            </Button>
          </Box>
          {!templateBalanced && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              Debits must equal credits and be greater than zero before saving.
            </Typography>
          )}
          {dialog.editing && Array.isArray(dialog.editing.lines) && dialog.editing.lines.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                Saved lines:{' '}
                {dialog.editing.lines.map((l, i) => (
                  <span key={i}>
                    {i > 0 ? ', ' : ''}{look.accountLabel(l.account_id, '') || lineAccountLabel(l, look) || '—'}
                  </span>
                ))}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, editing: null })}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving || !templateBalanced}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={preview.open} onClose={noBackdrop(() => setPreview({ open: false, rows: [] }))} maxWidth="sm" fullWidth>
        <DialogTitle>Due templates (dry run)</DialogTitle>
        <DialogContent>
          {preview.rows.length === 0 ? (
            <Typography color="text.secondary">Nothing due - preview creates nothing.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Template</TableCell><TableCell>Next run</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.template_name || r.template_number || 'Recurring template'}</TableCell>
                    <TableCell>{r.next_run_date ? formatDate(r.next_run_date) : formatDate(r.next_due_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview({ open: false, rows: [] })}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Journals = () => {
  const { can } = useAccessControl();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [tab, setTab] = useState('entries');

  const visibleTabs = useMemo(() => {
    const tabs = [{ key: 'entries', label: 'Entries' }];
    if (can(RESOURCES.LEDGER.GL, ACTIONS.READ)) {
      tabs.push({ key: 'reversals', label: 'Reversal Requests' });
      tabs.push({ key: 'recurring', label: 'Recurring' });
    }
    return tabs;
  }, [can]);

  if (!can(RESOURCES.LEDGER.GL, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view the general ledger.</Alert>;
  }

  return (
    <Box>
      <ModulePageHeader title="General Ledger Journals" subtitle="Draft, post, approve, void and reverse - with maker-checker reversals and recurring templates" />
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            {visibleTabs.map((t) => (<Tab key={t.key} value={t.key} label={t.label} />))}
          </Tabs>
        </CardContent>
      </Card>
      {tab === 'entries' && <EntriesTab can={can} showSnackbar={showSnackbar} confirm={confirm} />}
      {tab === 'reversals' && <ReversalRequestsTab can={can} showSnackbar={showSnackbar} confirm={confirm} />}
      {tab === 'recurring' && <RecurringTab can={can} showSnackbar={showSnackbar} confirm={confirm} />}
      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
};

export default Journals;
